import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { specsAPI, vehicleGalleryAPI, vehicleCategoriesAPI } from '../services/api';
import { Gauge, Radio, Zap, Plane, Weight, Ruler, Loader2, X, ZoomIn, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { usePageContent } from '../hooks/usePageContent';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper function to get localized field from specs
const getLocalizedSpec = (specs, field, language) => {
  if (!specs) return '';
  
  if (language === 'tr') {
    const trField = specs[`${field}_tr`];
    if (trField && trField.trim()) {
      return trField;
    }
  }
  
  return specs[field] || '';
};

// Helper function to get localized field from vehicle
const getLocalizedField = (item, field, language) => {
  if (!item) return '';
  if (language === 'tr') {
    const trField = item[`${field}_tr`];
    if (trField && trField.trim()) return trField;
  }
  return item[field] || '';
};

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api')) return `${API_BASE_URL}${url}`;
  return `${API_BASE_URL}/api/uploads/image/${url}`;
};

const Aircraft = () => {
  const [specs, setSpecs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [lightboxVehicle, setLightboxVehicle] = useState(null);
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { getContent } = usePageContent('aircraft');
  
  // Dynamic page title
  usePageTitle('nav.aircraft', 'Our Vehicles', 'Araçlarımız');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [specsData, vehiclesData, categoriesData] = await Promise.all([
          specsAPI.get().catch(() => null),
          vehicleGalleryAPI.getAll().catch(() => []),
          vehicleCategoriesAPI.getAll().catch(() => [])
        ]);
        if (specsData) setSpecs(specsData);
        setVehicles(vehiclesData || []);
        setCategories(categoriesData || []);
      } catch (err) {
        console.error('Failed to fetch data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Handle category click - navigate to category page
  const handleCategoryClick = (categoryId) => {
    if (categoryId === 'all') {
      setSelectedCategory('all');
    } else {
      // Navigate to dedicated category page
      navigate(`/vehicles/${categoryId}`);
    }
  };

  // Get localized category name
  const getCategoryName = (cat) => {
    if (language === 'tr' && cat.name_tr) return cat.name_tr;
    return cat.name;
  };

  // Close modals on ESC key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        setLightboxVehicle(null);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  // Default specs as fallback
  const defaultSpecs = {
    weight: "< 35 lbs",
    wingspan: "1.8 m",
    powerplant: "High-efficiency Electric Propulsion",
    autopilot: "Pixhawk Suite with Custom ArduPilot Configuration",
    communication: "915MHz Telemetry / 2.4GHz Control Link",
    endurance: "28 minutes (full payload)",
    maxSpeed: "65 km/h",
    cruiseSpeed: "45 km/h",
    payloadCapacity: "2.5 kg",
    operatingAltitude: "0-120 m AGL",
    thrustToWeight: "2.1:1",
    weightNote: "Optimized for Rapid Response",
    wingspanNote: "Optimized Aspect Ratio",
    autopilotNote: "Advanced Navigation",
    communicationNote: "Redundant Systems",
    tableNote_weight: "Competition Compliant",
    tableNote_wingspan: "High Aspect Ratio Design",
    tableNote_endurance: "Full Mission Capable",
    tableNote_payload: "Including Camera & Payload",
    tableNote_altitude: "Above Ground Level",
    tableNote_thrust: "Excellent Climb Rate",
    designCard1_title: "Aerodynamic Design",
    designCard1_description: "Optimized airfoil with high aspect ratio wing provides efficient lift generation and stability.",
    designCard2_title: "Propulsion System",
    designCard2_description: "Electric propulsion system with high-efficiency motor and optimized propeller for extended flight time.",
    designCard3_title: "Autonomous Navigation",
    designCard3_description: "GPS-guided waypoint navigation with real-time obstacle avoidance and precision landing capabilities.",
    designCard4_title: "Payload System",
    designCard4_description: "Precision payload delivery mechanism with 15cm accuracy from 50m altitude for storm response operations."
  };

  const currentSpecs = specs || defaultSpecs;
  const getSpec = (field) => getLocalizedSpec(currentSpecs, field, language) || currentSpecs[field] || '';

  // Filter vehicles by category
  const filteredVehicles = selectedCategory === 'all' 
    ? vehicles 
    : vehicles.filter(v => v.category === selectedCategory);

  // Use categories from API with fallback
  const displayCategories = categories.length > 0 ? categories : [
    { id: 'all', name: 'All Vehicles', name_tr: 'Tüm Araçlar' },
    { id: 'iha-s1', name: 'UAV-S1', name_tr: 'İHA-S1' },
    { id: 'iha-s2', name: 'UAV-S2', name_tr: 'İHA-S2' },
    { id: 'prototype', name: 'Prototype', name_tr: 'Prototip' }
  ];

  // Navigate lightbox
  const navigateLightbox = (direction) => {
    const currentIndex = filteredVehicles.findIndex(v => v.id === lightboxVehicle.id);
    let newIndex;
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % filteredVehicles.length;
    } else {
      newIndex = currentIndex - 1 < 0 ? filteredVehicles.length - 1 : currentIndex - 1;
    }
    setLightboxVehicle(filteredVehicles[newIndex]);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center" style={{ background: '#0F172A' }}>
        <Loader2 className="w-12 h-12 animate-spin" style={{ color: '#00F0FF' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" style={{ background: '#0F172A' }} data-testid="aircraft-page">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${getContent('hero_image') || 'https://images.unsplash.com/photo-1767133858907-41df33e02b6b?w=1920'}')`
          }}
        >
          <div className="absolute inset-0" style={{
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.85) 0%, rgba(30, 41, 59, 0.7) 50%, rgba(0, 240, 255, 0.3) 100%)'
          }}></div>
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-[7.6923%] text-center">
          <h1 className="display-huge mb-4" style={{
            color: '#F8FAFC',
            textShadow: '0 0 20px rgba(0, 240, 255, 0.5)'
          }}>{getContent('hero_title') || t('aircraft.hero.title')}</h1>
          <p className="display-medium mb-2" style={{
            color: '#00F0FF',
            textShadow: '0 0 15px rgba(0, 240, 255, 0.6)'
          }}>{getContent('hero_subtitle') || t('aircraft.hero.subtitle')}</p>
          <p className="body-large mt-6" style={{ color: '#F8FAFC', opacity: 0.9 }}>
            {getContent('hero_description') || t('aircraft.hero.description')}
          </p>
        </div>
      </section>

      {/* Technical Introduction */}
      <section className="py-24 px-[7.6923%]" style={{
        background: 'rgba(30, 41, 59, 0.4)',
        backdropFilter: 'blur(8px)'
      }}>
        <div className="max-w-[1400px] mx-auto">
          <div className="max-w-4xl mx-auto">
            <h2 className="display-medium mb-8 text-center" style={{ color: '#F8FAFC' }}>
              {t('aircraft.overview.title')}
            </h2>
            <p className="body-large leading-relaxed text-center mb-8" style={{ color: '#F8FAFC', opacity: 0.9 }}>
              {t('aircraft.overview.description')}
            </p>
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-24 px-[7.6923%]" style={{ background: '#0F172A' }}>
        <div className="max-w-[1400px] mx-auto">
          <h2 className="display-medium mb-12 text-center" style={{
            color: '#F8FAFC',
            textShadow: '0 0 15px rgba(0, 240, 255, 0.3)'
          }}>{t('aircraft.specs.title')}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Weight */}
            <div className="glass-card p-8 dark-transition dark-hover" data-testid="spec-weight">
              <div className="flex items-center gap-4 mb-4">
                <Weight size={28} style={{ color: '#00F0FF' }} />
                <h3 className="heading-2" style={{ color: '#F8FAFC' }}>{t('aircraft.specs.weight')}</h3>
              </div>
              <p className="body-large" style={{ color: '#00F0FF', textShadow: '0 0 10px rgba(0, 240, 255, 0.4)' }}>{currentSpecs.weight}</p>
              <p className="body-small mt-2" style={{ color: '#F8FAFC', opacity: 0.85 }}>{getSpec('weightNote')}</p>
            </div>

            {/* Wingspan */}
            <div className="glass-card p-8 dark-transition dark-hover" data-testid="spec-wingspan">
              <div className="flex items-center gap-4 mb-4">
                <Ruler size={28} style={{ color: '#00F0FF' }} />
                <h3 className="heading-2" style={{ color: '#F8FAFC' }}>{t('aircraft.specs.wingspan')}</h3>
              </div>
              <p className="body-large" style={{ color: '#00F0FF', textShadow: '0 0 10px rgba(0, 240, 255, 0.4)' }}>{currentSpecs.wingspan}</p>
              <p className="body-small mt-2" style={{ color: '#F8FAFC', opacity: 0.85 }}>{getSpec('wingspanNote')}</p>
            </div>

            {/* Powerplant */}
            <div className="glass-card p-8 dark-transition dark-hover" data-testid="spec-powerplant">
              <div className="flex items-center gap-4 mb-4">
                <Zap size={28} style={{ color: '#00F0FF' }} />
                <h3 className="heading-2" style={{ color: '#F8FAFC' }}>{t('aircraft.specNotes.powerplant')}</h3>
              </div>
              <p className="body-medium" style={{ color: '#00F0FF', textShadow: '0 0 10px rgba(0, 240, 255, 0.4)' }}>{getSpec('powerplant')}</p>
              <p className="body-small mt-2" style={{ color: '#F8FAFC', opacity: 0.85 }}>{getSpec('endurance')}</p>
            </div>

            {/* Autopilot */}
            <div className="glass-card p-8 dark-transition dark-hover" data-testid="spec-autopilot">
              <div className="flex items-center gap-4 mb-4">
                <Gauge size={28} style={{ color: '#00F0FF' }} />
                <h3 className="heading-2" style={{ color: '#F8FAFC' }}>{t('aircraft.specNotes.autopilot')}</h3>
              </div>
              <p className="body-medium" style={{ color: '#00F0FF', textShadow: '0 0 10px rgba(0, 240, 255, 0.4)' }}>{getSpec('autopilot')}</p>
              <p className="body-small mt-2" style={{ color: '#F8FAFC', opacity: 0.85 }}>{getSpec('autopilotNote')}</p>
            </div>

            {/* Communication */}
            <div className="glass-card p-8 dark-transition dark-hover" data-testid="spec-communication">
              <div className="flex items-center gap-4 mb-4">
                <Radio size={28} style={{ color: '#00F0FF' }} />
                <h3 className="heading-2" style={{ color: '#F8FAFC' }}>{t('aircraft.systems.communication.title')}</h3>
              </div>
              <p className="body-medium" style={{ color: '#00F0FF', textShadow: '0 0 10px rgba(0, 240, 255, 0.4)' }}>{getSpec('communication')}</p>
              <p className="body-small mt-2" style={{ color: '#F8FAFC', opacity: 0.85 }}>{getSpec('communicationNote')}</p>
            </div>

            {/* Performance */}
            <div className="glass-card p-8 dark-transition dark-hover" data-testid="spec-performance">
              <div className="flex items-center gap-4 mb-4">
                <Plane size={28} style={{ color: '#00F0FF' }} />
                <h3 className="heading-2" style={{ color: '#F8FAFC' }}>{t('aircraft.specNotes.performance')}</h3>
              </div>
              <p className="body-medium" style={{ color: '#00F0FF', textShadow: '0 0 10px rgba(0, 240, 255, 0.4)' }}>
                {t('aircraft.specNotes.maxSpeed')}: {currentSpecs.maxSpeed}<br />
                {t('aircraft.specs.speed')}: {currentSpecs.cruiseSpeed}
              </p>
              <p className="body-small mt-2" style={{ color: '#F8FAFC', opacity: 0.85 }}>
                {t('aircraft.specNotes.thrustToWeight')}: {currentSpecs.thrustToWeight}
              </p>
            </div>
          </div>

          {/* Full Specs Table */}
          <div className="mt-16 glass-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="specs-table">
                <thead>
                  <tr style={{ background: 'rgba(15, 23, 42, 0.8)', borderBottom: '1px solid rgba(0, 240, 255, 0.3)' }}>
                    <th className="text-left p-6 heading-3" style={{ color: '#F8FAFC' }}>{t('aircraft.table.parameter')}</th>
                    <th className="text-left p-6 heading-3" style={{ color: '#F8FAFC' }}>{t('aircraft.table.value')}</th>
                    <th className="text-left p-6 heading-3" style={{ color: '#F8FAFC' }}>{t('aircraft.table.notes')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid rgba(0, 240, 255, 0.2)' }}>
                    <td className="p-6 body-medium" style={{ color: '#F8FAFC' }}>{t('aircraft.specs.weight')}</td>
                    <td className="p-6 body-medium" style={{ color: '#00F0FF', textShadow: '0 0 8px rgba(0, 240, 255, 0.3)' }}>{currentSpecs.weight}</td>
                    <td className="p-6 body-small" style={{ color: '#F8FAFC', opacity: 0.85 }}>{getSpec('tableNote_weight')}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid rgba(0, 240, 255, 0.2)' }}>
                    <td className="p-6 body-medium" style={{ color: '#F8FAFC' }}>{t('aircraft.specs.wingspan')}</td>
                    <td className="p-6 body-medium" style={{ color: '#00F0FF', textShadow: '0 0 8px rgba(0, 240, 255, 0.3)' }}>{currentSpecs.wingspan}</td>
                    <td className="p-6 body-small" style={{ color: '#F8FAFC', opacity: 0.85 }}>{getSpec('tableNote_wingspan')}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid rgba(0, 240, 255, 0.2)' }}>
                    <td className="p-6 body-medium" style={{ color: '#F8FAFC' }}>{t('aircraft.specs.endurance')}</td>
                    <td className="p-6 body-medium" style={{ color: '#00F0FF', textShadow: '0 0 8px rgba(0, 240, 255, 0.3)' }}>{getSpec('endurance')}</td>
                    <td className="p-6 body-small" style={{ color: '#F8FAFC', opacity: 0.85 }}>{getSpec('tableNote_endurance')}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid rgba(0, 240, 255, 0.2)' }}>
                    <td className="p-6 body-medium" style={{ color: '#F8FAFC' }}>{t('aircraft.specs.payload')}</td>
                    <td className="p-6 body-medium" style={{ color: '#00F0FF', textShadow: '0 0 8px rgba(0, 240, 255, 0.3)' }}>{getSpec('payloadCapacity')}</td>
                    <td className="p-6 body-small" style={{ color: '#F8FAFC', opacity: 0.85 }}>{getSpec('tableNote_payload')}</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid rgba(0, 240, 255, 0.2)' }}>
                    <td className="p-6 body-medium" style={{ color: '#F8FAFC' }}>{t('aircraft.specs.ceiling')}</td>
                    <td className="p-6 body-medium" style={{ color: '#00F0FF', textShadow: '0 0 8px rgba(0, 240, 255, 0.3)' }}>{getSpec('operatingAltitude')}</td>
                    <td className="p-6 body-small" style={{ color: '#F8FAFC', opacity: 0.85 }}>{getSpec('tableNote_altitude')}</td>
                  </tr>
                  <tr>
                    <td className="p-6 body-medium" style={{ color: '#F8FAFC' }}>{t('aircraft.specNotes.thrustToWeight')}</td>
                    <td className="p-6 body-medium" style={{ color: '#00F0FF', textShadow: '0 0 8px rgba(0, 240, 255, 0.3)' }}>{currentSpecs.thrustToWeight}</td>
                    <td className="p-6 body-small" style={{ color: '#F8FAFC', opacity: 0.85 }}>{getSpec('tableNote_thrust')}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Design Rationale */}
      <section className="py-24 px-[7.6923%]" style={{ background: 'rgba(30, 41, 59, 0.4)', backdropFilter: 'blur(8px)' }}>
        <div className="max-w-[1400px] mx-auto">
          <h2 className="display-medium mb-16 text-center" style={{ color: '#F8FAFC' }}>{t('aircraft.design.title')}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {[1, 2, 3, 4].map(num => (
              <div key={num} className="glass-card p-8 dark-transition dark-hover">
                <h3 className="heading-2 mb-4" style={{ color: '#00F0FF', textShadow: '0 0 10px rgba(0, 240, 255, 0.4)' }}>
                  {getSpec(`designCard${num}_title`)}
                </h3>
                <p className="body-medium leading-relaxed" style={{ color: '#F8FAFC', opacity: 0.9 }}>
                  {getSpec(`designCard${num}_description`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vehicle Gallery - Enhanced */}
      <section className="py-24 px-[7.6923%]" style={{ background: '#0F172A' }} data-testid="vehicle-gallery-section">
        <div className="max-w-[1400px] mx-auto">
          <h2 className="display-medium mb-8 text-center" style={{ color: '#F8FAFC' }}>
            {t('aircraft.gallery.title')}
          </h2>
          
          {/* Category Filter Tabs */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {displayCategories.map(cat => (
              <button
                key={cat.id}
                onClick={() => handleCategoryClick(cat.id)}
                className="px-6 py-3 rounded-full font-medium transition-all duration-300"
                style={{
                  background: selectedCategory === cat.id 
                    ? 'linear-gradient(135deg, #00F0FF 0%, #06B6D4 100%)' 
                    : 'rgba(30, 41, 59, 0.6)',
                  color: selectedCategory === cat.id ? '#0F172A' : '#F8FAFC',
                  border: selectedCategory === cat.id 
                    ? 'none' 
                    : '1px solid rgba(0, 240, 255, 0.3)',
                  boxShadow: selectedCategory === cat.id 
                    ? '0 0 20px rgba(0, 240, 255, 0.4)' 
                    : 'none'
                }}
                data-testid={`filter-${cat.id}`}
              >
                {getCategoryName(cat)}
              </button>
            ))}
          </div>
          
          {/* Gallery Grid */}
          {filteredVehicles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredVehicles.map((vehicle) => (
                <div 
                  key={vehicle.id}
                  className="group relative rounded-xl overflow-hidden cursor-pointer"
                  style={{
                    background: 'rgba(30, 41, 59, 0.6)',
                    border: '1px solid rgba(0, 240, 255, 0.2)'
                  }}
                  data-testid={`vehicle-card-${vehicle.id}`}
                >
                  {/* Image with Hover Effect */}
                  <div 
                    className="relative h-64 overflow-hidden"
                    style={{ background: 'rgba(30, 41, 59, 0.4)' }}
                    onClick={() => setLightboxVehicle(vehicle)}
                  >
                    <img 
                      src={getImageUrl(vehicle.image)}
                      alt={getLocalizedField(vehicle, 'name', language)}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      style={{ objectPosition: 'center center' }}
                      onError={(e) => {
                        e.target.style.objectFit = 'contain';
                        e.target.style.padding = '16px';
                      }}
                    />
                    {/* Hover Overlay */}
                    <div 
                      className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none"
                      style={{ background: 'rgba(0, 0, 0, 0.7)' }}
                    >
                      <div className="text-center">
                        <h3 className="text-xl font-bold mb-2" style={{ color: '#00F0FF', textShadow: '0 0 15px rgba(0, 240, 255, 0.6)' }}>
                          {getLocalizedField(vehicle, 'name', language)}
                        </h3>
                        <div className="flex items-center justify-center gap-2" style={{ color: '#F8FAFC' }}>
                          <ZoomIn size={20} />
                          <span className="text-sm">{language === 'tr' ? 'Büyütmek için tıkla' : 'Click to enlarge'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Card Content */}
                  <div className="p-5">
                    <h3 className="font-bold text-lg mb-2" style={{ color: '#F8FAFC' }}>
                      {getLocalizedField(vehicle, 'name', language)}
                    </h3>
                    <p className="text-sm line-clamp-2 mb-4" style={{ color: '#94A3B8' }}>
                      {getLocalizedField(vehicle, 'description', language)}
                    </p>
                    <Link
                      to={`/vehicle/${vehicle.id}`}
                      className="w-full py-2.5 rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2"
                      style={{
                        background: 'rgba(0, 240, 255, 0.1)',
                        border: '1px solid rgba(0, 240, 255, 0.3)',
                        color: '#00F0FF'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = 'rgba(0, 240, 255, 0.2)';
                        e.currentTarget.style.boxShadow = '0 0 15px rgba(0, 240, 255, 0.3)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = 'rgba(0, 240, 255, 0.1)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      data-testid={`details-btn-${vehicle.id}`}
                    >
                      <ArrowRight size={18} />
                      {language === 'tr' ? 'Detayları Gör' : 'View Details'}
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16" style={{ color: '#94A3B8' }}>
              <Plane size={48} className="mx-auto mb-4 opacity-50" />
              <p>{language === 'tr' ? 'Bu kategoride araç bulunamadı' : 'No vehicles found in this category'}</p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxVehicle && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0, 0, 0, 0.95)', backdropFilter: 'blur(8px)' }}
          onClick={() => setLightboxVehicle(null)}
          data-testid="lightbox-modal"
        >
          <div className="relative max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            {/* Close Button */}
            <button
              onClick={() => setLightboxVehicle(null)}
              className="absolute -top-12 right-0 w-10 h-10 rounded-full flex items-center justify-center transition-colors"
              style={{ background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)' }}
            >
              <X size={20} style={{ color: '#F8FAFC' }} />
            </button>

            {/* Navigation Arrows */}
            {filteredVehicles.length > 1 && (
              <>
                <button
                  onClick={() => navigateLightbox('prev')}
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-14 w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                  style={{ background: 'rgba(0, 240, 255, 0.2)', border: '1px solid rgba(0, 240, 255, 0.3)' }}
                >
                  <ChevronLeft size={24} style={{ color: '#00F0FF' }} />
                </button>
                <button
                  onClick={() => navigateLightbox('next')}
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-14 w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                  style={{ background: 'rgba(0, 240, 255, 0.2)', border: '1px solid rgba(0, 240, 255, 0.3)' }}
                >
                  <ChevronRight size={24} style={{ color: '#00F0FF' }} />
                </button>
              </>
            )}

            {/* Image */}
            <img
              src={getImageUrl(lightboxVehicle.image)}
              alt={getLocalizedField(lightboxVehicle, 'name', language)}
              className="w-full h-auto max-h-[80vh] object-contain rounded-xl"
            />

            {/* Caption */}
            <div className="mt-4 text-center">
              <h3 className="text-2xl font-bold" style={{ color: '#00F0FF', textShadow: '0 0 15px rgba(0, 240, 255, 0.5)' }}>
                {getLocalizedField(lightboxVehicle, 'name', language)}
              </h3>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Aircraft;
